import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Twitter, Twitch, Youtube, Instagram } from "lucide-react";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { useGames } from "../context/GamesContext";

export default function PlayerProfile() {
  const { id } = useParams();
  const { labelOf } = useGames();
  const [player, setPlayer] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/players/${id}`)
      .then((r) => setPlayer(r.data))
      .catch(() => setError("Player not found"));
  }, [id]);

  if (error)
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-neutral-400" data-testid="player-profile-error">
        {error} — <Link to="/roster" className="text-axe-purple underline">back to roster</Link>
      </div>
    );
  if (!player)
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-neutral-400" data-testid="player-profile-loading">
        Loading player…
      </div>
    );

  const socials = [
    player.twitter && { icon: Twitter, label: "Twitter", href: player.twitter },
    player.twitch && { icon: Twitch, label: "Twitch", href: player.twitch },
    player.youtube && { icon: Youtube, label: "YouTube", href: player.youtube },
    player.instagram && { icon: Instagram, label: "Instagram", href: player.instagram },
  ].filter(Boolean);

  return (
    <div data-testid="player-profile" className="text-white">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-16">
        <Link
          to="/roster"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-axe-purple hover:text-white"
        >
          <ArrowLeft size={14} /> Back to roster
        </Link>

        <div className="mt-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-3xl border border-axe-border bg-axe-surface aspect-[4/5]">
              <img src={player.image} alt={player.ign} style={{ objectPosition: player.image_position || "50% 50%" }} className="h-full w-full object-cover" />
              <div className="absolute top-4 left-4">
                <Badge className="bg-axe-purple/90 text-white uppercase tracking-[0.22em] text-[10px] font-bold">
                  {player.rank || "Pro"}
                </Badge>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
              {labelOf(player.game)} · {player.role}
            </div>
            <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.03]">
              {player.ign}
            </h1>
            {player.country && (
              <div className="mt-3 text-sm uppercase tracking-[0.25em] text-neutral-500">
                Represents · {player.country}
              </div>
            )}

            {(player.secondary_games || []).length > 0 && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-axe-purple font-bold">
                  Also plays
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {player.secondary_games.map((g) => (
                    <Link
                      key={g}
                      to="/roster"
                      data-testid={`player-secondary-badge-${g}`}
                      className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold bg-axe-tangerine/15 text-axe-tangerine border border-axe-tangerine/40 hover:bg-axe-tangerine hover:text-black transition-colors"
                    >
                      {labelOf(g)}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              {[
                { k: "Game", v: labelOf(player.game) },
                { k: "Role", v: player.role },
                { k: "Rank", v: player.rank || "—" },
              ].map((s) => (
                <div key={s.k} className="rounded-2xl border border-axe-border bg-axe-surface p-4">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-axe-purple font-bold">
                    {s.k}
                  </div>
                  <div className="mt-1.5 font-display font-bold text-base text-white">{s.v}</div>
                </div>
              ))}
            </div>

            {player.bio && (
              <div className="mt-8">
                <div className="text-xs uppercase tracking-[0.3em] text-axe-tangerine font-bold">
                  Bio
                </div>
                <p className="mt-3 text-neutral-300 leading-relaxed whitespace-pre-line">
                  {player.bio}
                </p>
              </div>
            )}

            {socials.length > 0 && (
              <div className="mt-10">
                <div className="text-xs uppercase tracking-[0.3em] text-axe-tangerine font-bold">
                  Follow
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  {socials.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-axe-border bg-axe-surface px-5 py-2.5 text-xs uppercase tracking-[0.22em] font-bold hover:border-axe-purple/60"
                      data-testid={`player-social-${s.label.toLowerCase()}`}
                    >
                      <s.icon size={14} className="text-axe-tangerine" /> {s.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
